import { Plus } from '@phosphor-icons/react/dist/ssr/Plus'
import { useId, useState } from 'react'
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion'

export type CaseStudyFaqAccordionItem = {
  question: string
  answer: string
}

type Props = {
  items: CaseStudyFaqAccordionItem[]
}

/**
 * FAQ accordion aligned with portfolio FAQ spec (Figma node 100:12242):
 * Familjen titles, muted body, bottom dividers, plus rotates to “×” when open.
 */
export const CaseStudyFaqAccordion = ({ items }: Props) => {
  const baseId = useId()
  const reduceMotion = usePrefersReducedMotion()
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  const rowTransition = reduceMotion ? '' : 'transition-[grid-template-rows] duration-200 ease-out'
  const iconTransition = reduceMotion ? '' : 'transition-transform duration-200 ease-out'

  return (
    <div className="w-full bg-[var(--background)]">
      {items.map((item, i) => {
        const isOpen = openIndex === i
        const panelId = `${baseId}-panel-${i}`
        const headerId = `${baseId}-header-${i}`

        return (
          <div key={i} className="border-b border-[var(--border)]">
            <button
              type="button"
              id={headerId}
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => setOpenIndex((prev) => (prev === i ? null : i))}
              className="flex w-full items-center gap-6 py-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2"
            >
              <span className="min-w-0 flex-1 font-display text-[16px] font-normal leading-5 tracking-[-0.16px] text-[var(--foreground)] [text-wrap:pretty]">
                {item.question}
              </span>
              <span className="flex size-6 shrink-0 items-center justify-center" aria-hidden>
                <Plus
                  className={`size-6 text-[var(--foreground)] ${iconTransition} ${isOpen ? 'rotate-45' : ''}`}
                  weight="regular"
                />
              </span>
            </button>
            <div className={`grid ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'} ${rowTransition}`}>
              <div className="min-h-0 overflow-hidden">
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={headerId}
                  aria-hidden={!isOpen}
                  className="pb-5 text-[15px] leading-[22.5px] tracking-[-0.16px] text-[var(--foreground-muted)] [text-wrap:pretty]"
                >
                  {item.answer}
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
